import React from 'react';
import { useTranslation } from './i18n';

export default function VoorwaardenPage({ onBack, onNavigate }) {
  const { t } = useTranslation();

  const sections = [
    {
      title: t('terms_page.sections.definitions.title'),
      body: [t('terms_page.sections.definitions.body')],
      bullets: [
        t('terms_page.sections.definitions.item_1'),
        t('terms_page.sections.definitions.item_2'),
        t('terms_page.sections.definitions.item_3'),
        t('terms_page.sections.definitions.item_4'),
        t('terms_page.sections.definitions.item_5'),
        t('terms_page.sections.definitions.item_6'),
        t('terms_page.sections.definitions.item_7'),
      ],
    },
    {
      title: t('terms_page.sections.applicability.title'),
      body: [t('terms_page.sections.applicability.body')],
    },
    {
      title: t('terms_page.sections.role.title'),
      body: [t('terms_page.sections.role.body'), t('terms_page.sections.role.outro')],
      bullets: [
        t('terms_page.sections.role.item_1'),
        t('terms_page.sections.role.item_2'),
        t('terms_page.sections.role.item_3'),
      ],
    },
    {
      title: t('terms_page.sections.account.title'),
      bullets: [
        t('terms_page.sections.account.item_1'),
        t('terms_page.sections.account.item_2'),
        t('terms_page.sections.account.item_3'),
      ],
    },
    {
      title: t('terms_page.sections.matching.title'),
      body: [t('terms_page.sections.matching.outro')],
      bullets: [
        t('terms_page.sections.matching.item_1'),
        t('terms_page.sections.matching.item_2'),
        t('terms_page.sections.matching.item_3'),
      ],
    },
    {
      title: t('terms_page.sections.trial.title'),
      body: [t('terms_page.sections.trial.outro')],
      bullets: [
        t('terms_page.sections.trial.item_1'),
        t('terms_page.sections.trial.item_2'),
        t('terms_page.sections.trial.item_3'),
        t('terms_page.sections.trial.item_4'),
        t('terms_page.sections.trial.item_5'),
      ],
    },
    {
      title: t('terms_page.sections.obligations.title'),
      subsections: [
        {
          title: t('terms_page.sections.obligations.candidates_title'),
          bullets: [
            t('terms_page.sections.obligations.candidates_1'),
            t('terms_page.sections.obligations.candidates_2'),
            t('terms_page.sections.obligations.candidates_3'),
          ],
        },
        {
          title: t('terms_page.sections.obligations.employers_title'),
          bullets: [
            t('terms_page.sections.obligations.employers_1'),
            t('terms_page.sections.obligations.employers_2'),
            t('terms_page.sections.obligations.employers_3'),
          ],
        },
      ],
    },
    {
      title: t('terms_page.sections.fees.title'),
      bullets: [
        t('terms_page.sections.fees.item_1'),
        t('terms_page.sections.fees.item_2'),
      ],
    },
    {
      title: t('terms_page.sections.liability.title'),
      body: [t('terms_page.sections.liability.body'), t('terms_page.sections.liability.limit')],
      bullets: [
        t('terms_page.sections.liability.item_1'),
        t('terms_page.sections.liability.item_2'),
        t('terms_page.sections.liability.item_3'),
        t('terms_page.sections.liability.item_4'),
      ],
    },
    {
      title: t('terms_page.sections.availability.title'),
      bullets: [
        t('terms_page.sections.availability.item_1'),
        t('terms_page.sections.availability.item_2'),
      ],
    },
    {
      title: t('terms_page.sections.ip.title'),
      body: [t('terms_page.sections.ip.body')],
    },
    {
      title: t('terms_page.sections.privacy.title'),
      bodyPrefix: t('terms_page.sections.privacy.body_prefix'),
      bodyButton: t('terms_page.sections.privacy.link_label'),
      bodySuffix: t('terms_page.sections.privacy.body_suffix'),
    },
    {
      title: t('terms_page.sections.termination.title'),
      body: [t('terms_page.sections.termination.outro')],
      bullets: [
        t('terms_page.sections.termination.item_1'),
        t('terms_page.sections.termination.item_2'),
      ],
    },
    {
      title: t('terms_page.sections.company_fees.title'),
      bullets: [
        t('terms_page.sections.company_fees.item_1'),
        t('terms_page.sections.company_fees.item_2'),
        t('terms_page.sections.company_fees.item_3'),
        t('terms_page.sections.company_fees.item_4'),
      ],
    },
    {
      title: t('terms_page.sections.avoidance.title'),
      bullets: [
        t('terms_page.sections.avoidance.item_1'),
        t('terms_page.sections.avoidance.item_2'),
        t('terms_page.sections.avoidance.item_3'),
      ],
    },
    {
      title: t('terms_page.sections.governing_law.title'),
      body: [t('terms_page.sections.governing_law.body')],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logozondertekst.png" alt="Logo" className="h-8" />
            <span className="font-bold text-gray-900">WeRepresent</span>
          </div>
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            {t('terms_page.back_home')}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('terms_page.title')}</h1>
        <p className="text-gray-500 mb-8">{t('terms_page.version')}</p>
        
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-6 text-gray-700">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.title}</h2>
              {section.body?.map((paragraph) => (
                <p key={paragraph} className="mt-3 first:mt-0">{paragraph}</p>
              ))}
              {section.bullets?.length ? (
                <ul className="ml-4 list-disc space-y-1 mt-2">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
              {section.subsections?.map((subsection) => (
                <div key={subsection.title} className="mt-4">
                  <h3 className="font-medium text-gray-800 mb-2">{subsection.title}</h3>
                  <ul className="ml-4 list-disc space-y-1">
                    {subsection.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {section.bodyPrefix ? (
                <p>
                  {section.bodyPrefix}{' '}
                  <button 
                    onClick={() => onNavigate && onNavigate('/privacy')} 
                    className="text-blue-600 hover:underline"
                  >
                    {section.bodyButton}
                  </button>
                  {section.bodySuffix}
                </p>
              ) : null}
            </section>
          ))}
        </div>
      </main>

      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-400 text-sm">
          {t('terms_page.footer', { year: new Date().getFullYear() })}
        </div>
      </footer>
    </div>
  );
}
